import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';

interface ProgressIndicatorProps {
  value: number;
  max?: number;
  label?: string;
  showPercentage?: boolean;
  variant?: 'default' | 'success' | 'warning' | 'danger';
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  value,
  max = 100,
  label,
  showPercentage = true,
  variant = 'default',
  size = 'medium',
  className = ''
}) => {
  const { isDark } = useTheme();
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  const sizeClasses = {
    small: 'h-2',
    medium: 'h-3',
    large: 'h-4'
  };

  const variantColors = {
    default: 'bg-professional-blue-600',
    success: 'bg-green-600',
    warning: 'bg-yellow-600',
    danger: 'bg-red-600'
  };

  const backgroundColors = {
    default: isDark ? 'bg-white/10' : 'bg-gray-200',
    success: isDark ? 'bg-green-900/30' : 'bg-green-100',
    warning: isDark ? 'bg-yellow-900/30' : 'bg-yellow-100',
    danger: isDark ? 'bg-red-900/30' : 'bg-red-100'
  };

  return (
    <div className={className}>
      {(label || showPercentage) && (
        <div className="flex justify-between items-center mb-2">
          {label && (
            <span className={`text-sm font-medium ${
              isDark ? 'text-slate-300' : 'text-gray-700'
            }`}>
              {label}
            </span>
          )}
          {showPercentage && (
            <span className={`text-sm ${
              isDark ? 'text-slate-400' : 'text-gray-600'
            }`}>
              {Math.round(percentage)}%
            </span>
          )}
        </div>
      )}
      
      <div className={`w-full rounded-full overflow-hidden ${backgroundColors[variant]} ${sizeClasses[size]}`}>
        <motion.div
          className={`h-full rounded-full ${variantColors[variant]}`}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
};

export default ProgressIndicator;